import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import StudentService from "../service/StudentService";
import { normalizeRole, STUDENT_ROLES } from "../utils/authRole";

const StudentProfileContext = createContext({
  studentProfile: null,
  studentProfileLoading: false,
  studentProfileError: "",
});

export const StudentProfileProvider = ({ userRole, children }) => {
  const normalizedRole = normalizeRole(userRole);
  const isStudentRole = STUDENT_ROLES.includes(normalizedRole);
  const [studentProfile, setStudentProfile] = useState(() => StudentService.getCachedProfile());
  const [studentProfileLoading, setStudentProfileLoading] = useState(false);
  const [studentProfileError, setStudentProfileError] = useState("");

  useEffect(() => {
    let isMounted = true;

    if (!isStudentRole) {
      setStudentProfile(null);
      setStudentProfileLoading(false);
      setStudentProfileError("");
      return undefined;
    }

    const cached = StudentService.getCachedProfile();
    if (cached) {
      setStudentProfile(cached);
    }

    setStudentProfileLoading(!cached);
    setStudentProfileError("");

    StudentService.getProfile({ forceRefresh: !cached, preferCache: Boolean(cached) })
      .then((profile) => {
        if (!isMounted) return;
        setStudentProfile(profile);
        setStudentProfileError("");
      })
      .catch((error) => {
        if (!isMounted) return;
        setStudentProfileError(error?.message || "Unable to fetch student profile.");
      })
      .finally(() => {
        if (isMounted) {
          setStudentProfileLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isStudentRole]);

  const value = useMemo(() => ({
    studentProfile,
    studentProfileLoading,
    studentProfileError,
  }), [studentProfile, studentProfileLoading, studentProfileError]);

  return (
    <StudentProfileContext.Provider value={value}>
      {children}
    </StudentProfileContext.Provider>
  );
};

export const useStudentProfile = () => useContext(StudentProfileContext);